import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Spinner,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack
} from "@chakra-ui/react";
import {
  AddIcon,
  DeleteIcon,
  EditIcon,
  RepeatIcon,
  SearchIcon
} from "@chakra-ui/icons";
import {
  FaCamera,
  FaIdBadge,
  FaSyncAlt,
  FaUserCheck,
  FaUserSlash,
  FaVial
} from "react-icons/fa";
import {
  EXPECTED_EMBEDDING_DIM,
  fetchFaceImageWithAuth,
  formatDate,
  hasValidEncoding
} from "./faceUtils";

const FaceCard = ({
  face,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onReencode,
  onTestEncoding,
  reencodingId,
  testingId
}) => {
  const encoded = hasValidEncoding(face);
  const cardBg = "white";

  return (
    <Box
      bg={cardBg}
      borderRadius="xl"
      boxShadow="md"
      overflow="hidden"
      transition="all 0.2s ease"
      _hover={{ transform: "translateY(-4px)", boxShadow: "xl" }}
    >
      <Box position="relative" pb="100%" bg="gray.200">
        {face.imageUrl ? (
          <Image
            src={face.imageUrl}
            alt={face.name}
            position="absolute"
            top={0}
            left={0}
            w="100%"
            h="100%"
            objectFit="cover"
          />
        ) : (
          <Center position="absolute" top={0} left={0} w="100%" h="100%">
            <Avatar size="xl" name={face.name} />
          </Center>
        )}

        <Tooltip
          label={encoded
            ? `Encoding ${EXPECTED_EMBEDDING_DIM}-d ativo`
            : "Sem encoding válido — renove ou reenvie a foto"}
          hasArrow
        >
          <Badge
            position="absolute"
            top={2}
            right={2}
            colorScheme={encoded ? "green" : "orange"}
            borderRadius="full"
            px={2}
            py={1}
            display="flex"
            alignItems="center"
            gap="4px"
          >
            <Icon as={encoded ? FaUserCheck : FaUserSlash} boxSize={3} />
            {encoded ? "Ativo" : "Sem encoding"}
          </Badge>
        </Tooltip>
      </Box>

      <VStack align="stretch" spacing={2} p={4}>
        <Tooltip label={face.name} hasArrow openDelay={500}>
          <Text fontWeight="bold" fontSize="md" noOfLines={1}>{face.name}</Text>
        </Tooltip>

        <HStack spacing={2} minH="24px">
          {face.user?.name || face.user?.username ? (
            <Tag size="sm" colorScheme="teal" borderRadius="full" maxW="100%">
              <TagLeftIcon as={FaIdBadge} />
              <TagLabel noOfLines={1}>{face.user.name || face.user.username}</TagLabel>
            </Tag>
          ) : (
            <Text fontSize="xs" color="gray.400">Sem usuário vinculado</Text>
          )}
        </HStack>

        <Text fontSize="xs" color="gray.500">
          {face.createdAt ? `Cadastro: ${formatDate(face.createdAt)}` : ""}
        </Text>

        {canEdit && (
          <HStack spacing={1} pt={1}>
            <Tooltip label="Testar encoding" hasArrow>
              <IconButton
                aria-label="Testar encoding"
                icon={<FaVial />}
                size="sm"
                variant="outline"
                colorScheme="purple"
                isLoading={testingId === face.id}
                onClick={() => onTestEncoding(face)}
              />
            </Tooltip>
            <Tooltip label="Renovar encoding" hasArrow>
              <IconButton
                aria-label="Renovar encoding"
                icon={<FaSyncAlt />}
                size="sm"
                variant="outline"
                colorScheme="blue"
                isLoading={reencodingId === face.id}
                onClick={() => onReencode(face)}
              />
            </Tooltip>
          </HStack>
        )}

        <Flex justify="flex-end" align="center" pt={1}>
          <HStack spacing={1}>
            {canEdit && (
              <Tooltip label="Editar" hasArrow>
                <IconButton
                  aria-label="Editar rosto"
                  icon={<EditIcon />}
                  size="sm"
                  variant="ghost"
                  colorScheme="teal"
                  onClick={() => onEdit(face)}
                />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip label="Remover" hasArrow>
                <IconButton
                  aria-label="Remover rosto"
                  icon={<DeleteIcon />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => onDelete(face)}
                />
              </Tooltip>
            )}
          </HStack>
        </Flex>
      </VStack>
    </Box>
  );
};

const FaceManagementTab = ({ userLevel, userId }) => {
  const [faces, setFaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [reencodingId, setReencodingId] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testingFace, setTestingFace] = useState(null);
  const [testUpload, setTestUpload] = useState(null);

  const [editingFace, setEditingFace] = useState(null);
  const [formName, setFormName] = useState("");
  const [formPhoto, setFormPhoto] = useState(null);
  const [formPreview, setFormPreview] = useState("");
  const formModal = useDisclosure();
  const testModal = useDisclosure();
  const fileInputRef = useRef(null);
  const testFileInputRef = useRef(null);

  const [faceToDelete, setFaceToDelete] = useState(null);
  const deleteDialog = useDisclosure();
  const cancelDeleteRef = useRef(null);

  const toast = useToast();
  const canEdit = userLevel >= 3;
  const canDelete = userLevel >= 4;

  const fetchFaces = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/faces", {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Erro ao buscar faces");
      const data = await response.json();

      const facesWithBlobUrls = await Promise.all(
        data.map(async (face) => ({
          ...face,
          imageUrl: await fetchFaceImageWithAuth(face.image_url)
        }))
      );
      setFaces(facesWithBlobUrls);
    } catch (error) {
      toast({
        title: "Erro ao buscar rostos cadastrados",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchFaces();
  }, [fetchFaces]);

  const filteredFaces = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return faces;
    return faces.filter((face) =>
      (face.name || "").toLowerCase().includes(term)
      || (face.user?.name || "").toLowerCase().includes(term)
      || (face.user?.username || "").toLowerCase().includes(term)
    );
  }, [faces, search]);

  const encodedCount = useMemo(
    () => faces.filter(hasValidEncoding).length,
    [faces]
  );

  const openCreateModal = () => {
    setEditingFace(null);
    setFormName("");
    setFormPhoto(null);
    setFormPreview("");
    formModal.onOpen();
  };

  const openEditModal = (face) => {
    setEditingFace(face);
    setFormName(face.name || "");
    setFormPhoto(null);
    setFormPreview(face.imageUrl || "");
    formModal.onOpen();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFormPhoto(file);
    setFormPreview(URL.createObjectURL(file));
  };

  const handleSaveFace = async () => {
    if (!formName.trim()) {
      toast({ title: "Informe o nome da pessoa", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    if (!editingFace && !(formPhoto instanceof File)) {
      toast({ title: "Selecione uma foto do rosto", status: "warning", duration: 3000, isClosable: true });
      return;
    }

    const formData = new FormData();
    formData.append("name", formName.trim());
    if (!editingFace && userId) formData.append("user_id", userId);
    if (formPhoto instanceof File) formData.append("image", formPhoto);

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const url = editingFace ? `/api/faces/${editingFace.id}` : "/api/faces";
      const response = await fetch(url, {
        method: editingFace ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body?.error || "Erro ao salvar rosto");
      }

      if (body?.warning) {
        toast({
          title: "Rosto salvo, mas sem reconhecimento",
          description: body.warning,
          status: "warning",
          duration: 8000,
          isClosable: true
        });
      } else {
        toast({
          title: editingFace ? "Rosto atualizado" : "Rosto cadastrado",
          description: editingFace ? undefined : "Encoding facial gerado com sucesso.",
          status: "success",
          duration: 4000,
          isClosable: true
        });
      }
      formModal.onClose();
      fetchFaces();
    } catch (error) {
      toast({
        title: "Erro ao salvar rosto",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReencode = async (face) => {
    setReencodingId(face.id);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/faces/${face.id}/reencode`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.error || "Erro ao renovar encoding");

      toast({
        title: "Encoding renovado",
        description: `${face.name} — ${body.dimension}-d`,
        status: "success",
        duration: 4000
      });
      fetchFaces();
    } catch (error) {
      toast({
        title: "Erro ao renovar encoding",
        description: error.message,
        status: "error",
        duration: 5000
      });
    } finally {
      setReencodingId(null);
    }
  };

  const openTestModal = (face) => {
    setTestingFace(face);
    setTestResult(null);
    setTestUpload(null);
    testModal.onOpen();
  };

  const runTestEncoding = async (useUpload = false) => {
    if (!testingFace) return;
    setTestingId(testingFace.id);
    setTestResult(null);

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      if (useUpload && testUpload instanceof File) {
        formData.append("image", testUpload);
      }

      const response = await fetch(`/api/faces/${testingFace.id}/test-encoding`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: useUpload && testUpload instanceof File ? formData : undefined
      });
      const body = await response.json().catch(() => ({}));

      if (!response.ok || body.success === false) {
        throw new Error(body?.message || body?.error || "Falha no teste de encoding");
      }

      setTestResult(body);
    } catch (error) {
      setTestResult({ success: false, message: error.message });
    } finally {
      setTestingId(null);
    }
  };

  const confirmDelete = (face) => {
    setFaceToDelete(face);
    deleteDialog.onOpen();
  };

  const handleDeleteFace = async () => {
    if (!faceToDelete) return;
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/faces/${faceToDelete.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Erro ao remover rosto");
      toast({ title: `"${faceToDelete.name}" removido`, status: "success", duration: 3000, isClosable: true });
      fetchFaces();
    } catch (error) {
      toast({ title: "Erro ao remover rosto", status: "error", duration: 4000, isClosable: true });
    } finally {
      deleteDialog.onClose();
      setFaceToDelete(null);
    }
  };

  return (
    <Box>
      <Flex
        direction={{ base: "column", sm: "row" }}
        gap={3}
        align={{ base: "stretch", sm: "center" }}
        justify="space-between"
        mb={6}
      >
        <InputGroup maxW={{ base: "100%", sm: "320px" }} bg="white" borderRadius="md" boxShadow="sm">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Buscar por nome ou usuário..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>

        <HStack spacing={2} wrap="wrap">
          <Badge colorScheme="teal" borderRadius="full" px={3} py={1}>
            {faces.length} cadastrado{faces.length === 1 ? "" : "s"}
          </Badge>
          <Badge colorScheme="green" borderRadius="full" px={3} py={1}>
            {encodedCount} ativo{encodedCount === 1 ? "" : "s"}
          </Badge>
          {faces.length - encodedCount > 0 && (
            <Badge colorScheme="orange" borderRadius="full" px={3} py={1}>
              {faces.length - encodedCount} sem encoding
            </Badge>
          )}
          <Tooltip label="Atualizar lista" hasArrow>
            <IconButton
              aria-label="Atualizar"
              icon={<RepeatIcon />}
              variant="outline"
              colorScheme="teal"
              onClick={fetchFaces}
              isLoading={loading}
            />
          </Tooltip>
          {canEdit && (
            <Button leftIcon={<AddIcon />} colorScheme="teal" onClick={openCreateModal}>
              Adicionar Rosto
            </Button>
          )}
        </HStack>
      </Flex>

      {loading ? (
        <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5, xl: 6 }} spacing={5}>
          {Array.from({ length: 12 }).map((_, index) => (
            <Box key={index} borderRadius="xl" overflow="hidden" boxShadow="md" bg="white">
              <Skeleton pb="100%" />
              <Box p={4}>
                <Skeleton height="16px" mb={2} />
                <Skeleton height="12px" width="60%" />
              </Box>
            </Box>
          ))}
        </SimpleGrid>
      ) : filteredFaces.length === 0 ? (
        <Center py={20} flexDirection="column" gap={4} bg="white" borderRadius="lg" boxShadow="sm">
          <Icon as={FaCamera} boxSize={12} color="gray.300" />
          <Text color="gray.500" fontSize="lg" textAlign="center">
            {search ? `Nenhum rosto encontrado para "${search}"` : "Nenhum rosto cadastrado ainda"}
          </Text>
          {canEdit && !search && (
            <Button leftIcon={<AddIcon />} colorScheme="teal" variant="outline" onClick={openCreateModal}>
              Cadastrar o primeiro rosto
            </Button>
          )}
        </Center>
      ) : (
        <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5, xl: 6 }} spacing={5}>
          {filteredFaces.map((face) => (
            <FaceCard
              key={face.id}
              face={face}
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={openEditModal}
              onDelete={confirmDelete}
              onReencode={handleReencode}
              onTestEncoding={openTestModal}
              reencodingId={reencodingId}
              testingId={testingId}
            />
          ))}
        </SimpleGrid>
      )}

      <Modal isOpen={formModal.isOpen} onClose={saving ? () => {} : formModal.onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>
            {editingFace ? `Editar "${editingFace.name}"` : "Adicionar Novo Rosto"}
          </ModalHeader>
          <ModalCloseButton isDisabled={saving} />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Nome da pessoa</FormLabel>
                <Input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Ex.: João da Silva"
                />
              </FormControl>

              <FormControl isRequired={!editingFace}>
                <FormLabel>
                  Foto do rosto
                  {editingFace && (
                    <Text as="span" fontSize="xs" color="gray.500" ml={2}>
                      (opcional — reenviar regenera o encoding)
                    </Text>
                  )}
                </FormLabel>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  display="none"
                />
                <Box
                  borderWidth={2}
                  borderStyle="dashed"
                  borderColor={formPreview ? "teal.300" : "gray.300"}
                  borderRadius="lg"
                  p={4}
                  textAlign="center"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ borderColor: "teal.400", bg: "teal.50" }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {formPreview ? (
                    <VStack spacing={2}>
                      <Image
                        src={formPreview}
                        alt="Preview"
                        boxSize="140px"
                        objectFit="cover"
                        borderRadius="full"
                        mx="auto"
                        boxShadow="md"
                      />
                      <Text fontSize="sm" color="teal.600">Clique para trocar a foto</Text>
                    </VStack>
                  ) : (
                    <VStack spacing={2} py={4}>
                      <Icon as={FaCamera} boxSize={8} color="gray.400" />
                      <Text fontSize="sm" color="gray.500">Clique para selecionar uma foto</Text>
                      <Text fontSize="xs" color="gray.400">
                        Use uma foto frontal, nítida e com apenas um rosto
                      </Text>
                    </VStack>
                  )}
                </Box>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={formModal.onClose} isDisabled={saving}>Cancelar</Button>
            <Button
              colorScheme="teal"
              onClick={handleSaveFace}
              isLoading={saving}
              loadingText="Processando rosto..."
              spinner={<Spinner size="sm" />}
            >
              {editingFace ? "Salvar alterações" : "Cadastrar"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={testModal.isOpen} onClose={testModal.onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>Testar encoding — {testingFace?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color="gray.600">
                Gera um embedding de teste sem alterar o cadastro. Use a foto atual ou envie outra imagem.
              </Text>

              <Input
                ref={testFileInputRef}
                type="file"
                accept="image/*"
                display="none"
                onChange={(e) => setTestUpload(e.target.files?.[0] || null)}
              />

              <HStack>
                <Button
                  colorScheme="purple"
                  leftIcon={<FaVial />}
                  onClick={() => runTestEncoding(false)}
                  isLoading={testingId === testingFace?.id}
                >
                  Testar foto cadastrada
                </Button>
                <Button variant="outline" onClick={() => testFileInputRef.current?.click()}>
                  Escolher outra foto
                </Button>
              </HStack>

              {testUpload && (
                <Button
                  colorScheme="blue"
                  onClick={() => runTestEncoding(true)}
                  isLoading={testingId === testingFace?.id}
                >
                  Testar foto selecionada
                </Button>
              )}

              {testResult && (
                <Box
                  p={4}
                  borderRadius="md"
                  bg={testResult.success ? "green.50" : "red.50"}
                  borderWidth="1px"
                  borderColor={testResult.success ? "green.200" : "red.200"}
                >
                  {testResult.success ? (
                    <VStack align="start" spacing={1} fontSize="sm">
                      <Text fontWeight="bold" color="green.700">Encoding gerado com sucesso</Text>
                      <Text>Origem: {testResult.source === "upload" ? "upload" : "cadastro"}</Text>
                      <Text>Dimensão: {testResult.dimension}-d</Text>
                      <Text>Norma L2: {testResult.norm}</Text>
                      <Text>Amostra: [{testResult.sample?.join(", ")}]</Text>
                      <Text>
                        Encoding salvo: {testResult.stored_encoding_valid ? "válido" : "inválido/ausente"}
                        {testResult.stored_encoding_dimension
                          ? ` (${testResult.stored_encoding_dimension}-d)`
                          : ""}
                      </Text>
                    </VStack>
                  ) : (
                    <Text fontSize="sm" color="red.700">{testResult.message}</Text>
                  )}
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={testModal.onClose}>Fechar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={deleteDialog.onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remover rosto
            </AlertDialogHeader>
            <AlertDialogBody>
              Tem certeza que deseja remover <b>{faceToDelete?.name}</b>?
              Os alertas já gerados serão mantidos, mas a pessoa deixará de ser reconhecida pelas câmeras.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} onClick={deleteDialog.onClose}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleDeleteFace} ml={3}>Remover</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default FaceManagementTab;
